import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';

// SYN-2534: how often a projected window pings its opener.
// SYN-2538: lowered 3s → 1s so the dashboard detects a post-refresh popup
// close in ~4s (its timeout dropped 10s → 4s) instead of ~10-15s. The
// projector window is always foreground (it's projecting to a screen) so it
// isn't subject to background timer throttling; the dashboard's 4s timeout
// still tolerates ~3 missed 1s pings before reverting.
const HEARTBEAT_INTERVAL_MS = 1_000;

/**
 * SYN-2534: when this app is running inside a projector window opened by the
 * admin dashboard (primary or secondary screen), emit a periodic
 * `PROJECTOR_ALIVE` ping to `window.opener`.
 *
 * The dashboard opens these windows and normally close-detects them by
 * polling the `Window.closed` handle it holds. That handle is lost when the
 * operator refreshes the dashboard, so after a refresh the dashboard can no
 * longer tell when the projector window is closed — leaving the Centralized
 * View stuck on "Online & Projecting". These pings let the refreshed
 * dashboard re-establish liveness purely by listening (no `window.open`
 * handle needed): while pings arrive the projection is live; when they stop,
 * the window has closed and the dashboard reverts CV to "Online".
 *
 * No-op on the normal audience view (no opener).
 */
@Injectable({ providedIn: 'root' })
export class ProjectorHeartbeatService implements OnDestroy {
  private readonly _zone = inject(NgZone);
  private _timer: ReturnType<typeof setInterval> | null = null;

  /** Begin emitting heartbeats if this page is a projected window. Safe to
   *  call once at app bootstrap; idempotent. */
  start(): void {
    const opener = window.opener as Window | null;
    // Not opened by the dashboard (or opener already gone) → normal audience
    // view, nothing to announce.
    if (!opener || opener === window) {
      return;
    }
    if (this._timer) {
      return;
    }

    // Role is fixed for the lifetime of the window: the secondary screen is
    // opened with ?isPrimaryScreen=false; everything else (the session
    // primary view with ?isPrimaryScreen=true, and the /primary-screen
    // alternative-content route which carries no such param) is the primary.
    const params = new URLSearchParams(window.location.search);
    const screen =
      params.get('isPrimaryScreen') === 'false' ? 'secondary' : 'primary';

    const ping = (): void => {
      try {
        // targetOrigin '*' is acceptable for a contentless liveness ping; the
        // dashboard validates `event.origin` against the insights origin
        // before trusting it, so a foreign origin can't spoof projection
        // state.
        opener.postMessage({ type: 'PROJECTOR_ALIVE', screen }, '*');
      } catch {
        // Opener navigated away or cross-origin access threw — harmless, the
        // next tick retries.
      }
    };

    ping();
    // Outside Angular so the recurring timer never schedules change detection.
    this._zone.runOutsideAngular(() => {
      this._timer = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    });
  }

  ngOnDestroy(): void {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }
}
